import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Image as ImageIcon,
  LoaderCircle,
  RefreshCw,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, type ChangeEvent } from 'react';
import { optimizeImageFile } from './ImageOptimization';

export type CanonicalPhotoStatus = 'empty' | 'ready' | 'loading' | 'success' | 'error';

export type CanonicalPhotoFieldProps = {
  label: string;
  previewUrl?: string | null;
  fallbackText?: string;
  file?: File | null;
  accept?: string;
  capture?: boolean | 'user' | 'environment';
  helpText?: string;
  error?: string;
  warning?: string;
  status?: CanonicalPhotoStatus;
  readOnly?: boolean;
  disabled?: boolean;
  canRemove?: boolean;
  imageAlt?: string;
  onFileChange?: (file: File | null) => void;
  onRemove?: () => void;
  onRetry?: () => void;
};

export function CanonicalPhotoField({
  label,
  previewUrl,
  fallbackText = '',
  file = null,
  accept = 'image/jpeg,image/png,image/webp',
  capture,
  helpText = 'JPG, PNG o WebP.',
  error,
  warning,
  status = previewUrl || file ? 'ready' : 'empty',
  readOnly = false,
  disabled = false,
  canRemove = false,
  imageAlt = 'Fotografía registrada',
  onFileChange,
  onRemove,
  onRetry,
}: CanonicalPhotoFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const localUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  const visibleUrl = localUrl ?? previewUrl ?? null;
  const busy = status === 'loading';
  const interactive = !readOnly;

  useEffect(() => {
    return () => {
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [localUrl]);

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const candidate = event.target.files?.[0] ?? null;
    if (!candidate) onFileChange?.(null);
    else if (typeof createImageBitmap !== 'function') onFileChange?.(candidate);
    else void optimizeImageFile(candidate).then((optimized) => onFileChange?.(optimized));
    event.target.value = '';
  };

  const openPicker = () => {
    if (interactive && !busy) inputRef.current?.click();
  };

  const actionLabel = visibleUrl
    ? `Reemplazar ${label.toLowerCase()}`
    : `Agregar ${label.toLowerCase()}`;

  return (
    <section
      className="ui-photo-field"
      data-status={status}
      data-disabled={disabled || undefined}
      data-read-only={readOnly || undefined}
      aria-labelledby={`${inputId}-label`}
    >
      <div className="ui-photo-field__heading">
        <div>
          <strong id={`${inputId}-label`}>{label}</strong>
          {helpText ? <span>{helpText}</span> : null}
        </div>
        {status === 'success' ? (
          <span className="ui-photo-field__badge" data-tone="success">
            <CheckCircle2 aria-hidden="true" /> Lista
          </span>
        ) : null}
      </div>

      <div className="ui-photo-field__body">
        <div className="ui-photo-field__preview">
          {visibleUrl ? (
            <img src={visibleUrl} alt={imageAlt} />
          ) : fallbackText ? (
            <span aria-label="Sin fotografía">{fallbackText.slice(0, 3).toUpperCase()}</span>
          ) : (
            <ImageIcon aria-label="Sin fotografía" />
          )}
          {busy ? (
            <span className="ui-photo-field__loading" aria-hidden="true">
              <LoaderCircle />
            </span>
          ) : null}
        </div>

        <div className="ui-photo-field__content">
          {file ? (
            <div className="ui-photo-field__file" aria-live="polite">
              <strong>{file.name}</strong>
              <span>{formatFileSize(file.size)}</span>
            </div>
          ) : (
            <p>{visibleUrl ? 'Fotografía disponible.' : 'Aún no hay una fotografía.'}</p>
          )}

          {interactive ? (
            <div className="ui-photo-field__actions">
              <input
                ref={inputRef}
                id={inputId}
                className="ui-photo-field__input"
                type="file"
                accept={accept}
                capture={capture}
                disabled={disabled || busy}
                aria-label={actionLabel}
                onChange={selectFile}
              />
              <button
                type="button"
                className="ui-button ui-button--primary"
                onClick={openPicker}
                disabled={disabled || busy}
                aria-label={actionLabel}
              >
                {visibleUrl ? <RefreshCw aria-hidden="true" /> : <Camera aria-hidden="true" />}
                {visibleUrl ? 'Reemplazar' : 'Agregar foto'}
              </button>
              {file ? (
                <button
                  type="button"
                  className="ui-button ui-button--secondary"
                  onClick={() => onFileChange?.(null)}
                  disabled={disabled || busy}
                >
                  <RotateCcw aria-hidden="true" /> Descartar selección
                </button>
              ) : null}
              {canRemove && visibleUrl && !file ? (
                <button
                  type="button"
                  className="ui-photo-field__danger-action"
                  onClick={onRemove}
                  disabled={disabled || busy}
                >
                  <Trash2 aria-hidden="true" /> Retirar
                </button>
              ) : null}
              {status === 'error' && onRetry ? (
                <button
                  type="button"
                  className="ui-button ui-button--secondary"
                  onClick={onRetry}
                  disabled={disabled || busy}
                >
                  <RefreshCw aria-hidden="true" /> Reintentar
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="ui-photo-field__messages" aria-live="polite">
        {busy ? (
          <p role="status">
            <LoaderCircle aria-hidden="true" /> Procesando fotografía…
          </p>
        ) : null}
        {warning ? (
          <p data-tone="warning">
            <AlertTriangle aria-hidden="true" /> {warning}
          </p>
        ) : null}
        {error ? (
          <p data-tone="error" role="alert">
            <AlertTriangle aria-hidden="true" /> {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function formatFileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
